import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';
import { attendanceService } from '../services/attendanceService';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  Coffee,
  Timer,
  Calendar,
  User,
  RefreshCw,
} from 'lucide-react';

interface CheckinStatus {
  isCheckedIn: boolean;
  lastCheckin?: Date;
  lastCheckout?: Date;
  isOnBreak?: boolean;
  breakStartTime?: string;
}

interface TodayHours {
  totalHours: number;
  breakTime: number;
  overtime: number;
}

const ModernCheckinCheckout: React.FC = () => {
  console.log('🔍 ModernCheckinCheckout component rendering...');
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user } = useUserStore();

  console.log('🔍 User in component:', user);

  const [status, setStatus] = useState<CheckinStatus>({ isCheckedIn: false });
  const [todayHours, setTodayHours] = useState<TodayHours>({
    totalHours: 0,
    breakTime: 0,
    overtime: 0,
  });
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [notes, setNotes] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());

  // Dialog states
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Load initial status and hours
  useEffect(() => {
    console.log('🔍 useEffect triggered, user:', user);
    console.log('🔍 User ID check:', user?.id);
    console.log('🔍 User ID type:', typeof user?.id);
    console.log('🔍 User ID truthy:', !!user?.id);

    console.log('🔍 SIMPLE: About to call loadStatus...');
    loadStatus();
    console.log('🔍 SIMPLE: loadStatus called');
  }, [user?.id]);

  // Refresh status every 30 seconds to keep it in sync
  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(() => {
      loadStatus();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [user?.id]);

  const loadStatus = async () => {
    console.log('🔍 loadStatus called, user ID:', user?.id);
    if (!user?.id) {
      console.log('🔍 No user ID, returning from loadStatus');
      return;
    }

    try {
      console.log('🔍 Fetching today attendance for user:', user.id);
      const records = await attendanceService.getTodayAttendance(
        String(user.id)
      );
      console.log('🔍 Attendance records received:', records);
      console.log('🔍 Records length:', records.length);
      console.log(
        '🔍 Records details:',
        records.map((r) => ({ date: r.date, isIn: r.isIn }))
      );

      // Sort records by date to get the most recent one
      const sortedRecords = records.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );

      const lastRecord = sortedRecords[0];

      // User is checked in if the most recent record is a check-in
      const isCheckedIn = lastRecord?.isIn || false;

      // Find the last check-in and check-out times
      const lastCheckin = sortedRecords.find((record) => record.isIn);
      const lastCheckout = sortedRecords.find((record) => !record.isIn);

      // Load break status from backend (with fallback)
      let breakStatus: {
        isOnBreak: boolean;
        breakStartTime?: string;
        currentBreakDuration?: number;
      } = { isOnBreak: false, breakStartTime: undefined };
      try {
        breakStatus = await attendanceService.getBreakStatus(String(user?.id));
        console.log('🔍 Break status loaded from backend:', breakStatus);
      } catch (error) {
        console.log(
          'Could not load break status from backend, using default:',
          error
        );
        // Keep default values (not on break)
      }

      setStatus({
        isCheckedIn,
        lastCheckin: lastCheckin ? new Date(lastCheckin.date) : undefined,
        lastCheckout: lastCheckout ? new Date(lastCheckout.date) : undefined,
        isOnBreak: breakStatus.isOnBreak,
        breakStartTime: breakStatus.breakStartTime,
      });

      console.log('🔍 Loaded status:', {
        isCheckedIn,
        recordsCount: records.length,
        lastRecord: lastRecord
          ? { date: lastRecord.date, isIn: lastRecord.isIn }
          : null,
        allRecords: records.map((r) => ({ date: r.date, isIn: r.isIn })),
      });
    } catch (error) {
      console.error('Error loading status:', error);
      // Fallback to mock data
      setStatus({
        isCheckedIn: false,
        lastCheckin: new Date(Date.now() - 8 * 60 * 60 * 1000), // 8 hours ago
        lastCheckout: new Date(Date.now() - 1 * 60 * 60 * 1000), // 1 hour ago
      });
    }
  };

  const loadTodayHours = async () => {
    if (!user?.id) return;

    try {
      const records = await attendanceService.getTodayAttendance(
        String(user.id)
      );

      console.log('🔍 Calculating hours from records:', records.length);

      // Sort records by date
      const sortedRecords = records.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      let totalWorkHours = 0;
      let totalBreakTime = 0;
      let currentWorkStart: Date | null = null;
      let currentBreakStart: Date | null = null;
      let isCurrentlyWorking = false;
      let isCurrentlyOnBreak = false;

      // Process each record chronologically
      for (const record of sortedRecords) {
        const recordTime = new Date(record.date);

        if (record.isIn && !record.isOnBreak) {
          // Starting work
          if (currentWorkStart) {
            // End previous work session
            totalWorkHours +=
              (recordTime.getTime() - currentWorkStart.getTime()) /
              (1000 * 60 * 60);
          }
          currentWorkStart = recordTime;
          isCurrentlyWorking = true;
          isCurrentlyOnBreak = false;
          console.log('🔍 Work started at:', recordTime.toISOString());
        } else if (!record.isIn && !record.isOnBreak) {
          // Ending work
          if (currentWorkStart) {
            totalWorkHours +=
              (recordTime.getTime() - currentWorkStart.getTime()) /
              (1000 * 60 * 60);
            currentWorkStart = null;
          }
          isCurrentlyWorking = false;
          console.log('🔍 Work ended at:', recordTime.toISOString());
        } else if (record.isOnBreak) {
          // Starting break
          if (currentBreakStart) {
            // End previous break
            totalBreakTime +=
              (recordTime.getTime() - currentBreakStart.getTime()) /
              (1000 * 60 * 60);
          }
          currentBreakStart = recordTime;
          isCurrentlyOnBreak = true;
          console.log('🔍 Break started at:', recordTime.toISOString());
        } else if (!record.isOnBreak && currentBreakStart) {
          // Ending break
          totalBreakTime +=
            (recordTime.getTime() - currentBreakStart.getTime()) /
            (1000 * 60 * 60);
          currentBreakStart = null;
          isCurrentlyOnBreak = false;
          console.log('🔍 Break ended at:', recordTime.toISOString());
        }
      }

      // Handle current session (if still working or on break)
      const now = new Date();
      if (isCurrentlyWorking && currentWorkStart) {
        totalWorkHours +=
          (now.getTime() - currentWorkStart.getTime()) / (1000 * 60 * 60);
        console.log('🔍 Adding current work session');
      }
      if (isCurrentlyOnBreak && currentBreakStart) {
        totalBreakTime +=
          (now.getTime() - currentBreakStart.getTime()) / (1000 * 60 * 60);
        console.log('🔍 Adding current break session');
      }

      // Calculate overtime (assuming 8 hours is standard)
      const standardHours = 8;
      const overtime = Math.max(0, totalWorkHours - standardHours);

      console.log('🔍 Calculated hours:', {
        totalWorkHours: totalWorkHours.toFixed(2),
        totalBreakTime: totalBreakTime.toFixed(2),
        overtime: overtime.toFixed(2),
      });

      setTodayHours({
        totalHours: totalWorkHours,
        breakTime: totalBreakTime,
        overtime: overtime,
      });
    } catch (error) {
      console.error('Error loading today hours:', error);
      // Fallback to mock data
      setTodayHours({
        totalHours: 7.5,
        breakTime: 0.5,
        overtime: 0,
      });
    }
  };

  const refreshStatus = async () => {
    console.log('🔄 Refreshing status...');
    setLoading(true);
    try {
      console.log('🔄 About to call loadStatus...');
      await loadStatus();
      console.log('🔄 loadStatus completed, calling loadTodayHours...');
      await loadTodayHours();
      console.log('🔄 loadTodayHours completed');
      notify('Status refreshed successfully', { type: 'success' });
    } catch (error) {
      console.error('Error refreshing status:', error);
      notify('Failed to refresh status', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.warn('Location access denied or failed:', error);
        }
      );
    }
  };

  const handleCheckIn = async () => {
    if (!user?.id) return;

    console.log(
      '🔍 handleCheckIn called for user:',
      user.id,
      'location:',
      location
    );
    setLoading(true);
    try {
      console.log('🔍 Calling attendanceService.checkIn...');
      await attendanceService.checkIn(
        String(user.id),
        location ? `${location.lat},${location.lng}` : undefined
      );
      console.log('🔍 Check-in successful, updating status...');
      setStatus((prev) => ({
        ...prev,
        isCheckedIn: true,
        lastCheckin: new Date(),
      }));
      setNotes('');
      notify(translate('pos.common.message.success', 'Checked in successfully!'), {
        type: 'success',
      });

      // Reload status and hours after a longer delay to allow database to propagate
      setTimeout(async () => {
        console.log('🔍 Reloading status after check-in...');
        await loadStatus();
        await loadTodayHours();
      }, 3000); // 3 second delay to allow database propagation
    } catch (error) {
      console.error('❌ Error checking in:', error);
      notify('Failed to check in', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    if (!user?.id) return;

    console.log(
      '🔍 handleCheckOut called for user:',
      user.id,
      'location:',
      location
    );
    setLoading(true);
    try {
      console.log('🔍 Calling attendanceService.checkOut...');
      await attendanceService.checkOut(
        String(user.id),
        location ? `${location.lat},${location.lng}` : undefined
      );
      console.log('🔍 Check-out successful, updating status...');
      setStatus((prev) => ({
        ...prev,
        isCheckedIn: false,
        lastCheckout: new Date(),
      }));
      setNotes('');
      notify(translate('pos.common.message.success', 'Checked out successfully!'), {
        type: 'success',
      });

      // Reload status and hours after a longer delay to allow database to propagate
      setTimeout(async () => {
        console.log('🔍 Reloading status after check-out...');
        await loadStatus();
        await loadTodayHours();
      }, 3000); // 3 second delay to allow database propagation
    } catch (error) {
      console.error('❌ Error checking out:', error);
      notify('Failed to check out', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatDuration = (hours: number) => {
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const handleBreakTime = () => {
    // Show break time information and options
    if (!status.isCheckedIn) {
      notify(
        translate(
          'pos.staff.attendance.break_dialog_need_checkin',
          'You need to check in first before taking a break.'
        ),
        {
          type: 'warning',
        }
      );
      // Also show alert for immediate feedback
      alert(
        translate(
          'pos.staff.attendance.break_dialog_need_checkin',
          'You need to check in first before taking a break.'
        )
      );
      return;
    }

    // Show break time options
    notify(
      translate('pos.staff.attendance.break_quick_action_label', 'Break Time'),
      {
        type: 'info',
      }
    );
    // Also show alert for immediate feedback
    alert(
      translate('pos.staff.attendance.break_quick_action_label', 'Break Time')
    );
    // TODO: Implement break time tracking functionality
  };

  const handleViewTimesheet = () => {
    // Navigate to attendance/timesheet tab
    notify(
      translate(
        'pos.staff.attendance.view_timesheet_opening',
        'Opening timesheet...'
      ),
      { type: 'info' }
    );

    // Try to find and click the attendance tab
    const attendanceTab = document.querySelector(
      '[data-testid="attendance-tab"]'
    );
    if (attendanceTab) {
      (attendanceTab as HTMLElement).click();
      alert(
        translate(
          'pos.staff.attendance.view_timesheet_opening',
          'Opening timesheet...'
        )
      );
    } else {
      // Fallback: show message about navigating to attendance
      notify(
        translate(
          'pos.staff.attendance.view_timesheet_navigate_message',
          'Please navigate to the Attendance tab to view your timesheet'
        ),
        {
          type: 'info',
        }
      );
      alert(
        translate(
          'pos.staff.attendance.view_timesheet_navigate_message',
          'Please navigate to the Attendance tab to view your timesheet'
        )
      );
    }
  };

  if (!user) {
    return (
      <div className="space-y-6 py-6">
        <div className="text-center">
          <p className="text-muted-foreground">
            {translate(
              'pos.staff.attendance.login_required_message',
              'Please log in to access check-in/check-out'
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground">
          {translate('pos.staff.attendance.title', 'Check-in / Check-out')}
        </h1>
        <p className="text-muted-foreground mt-2">
          {translate(
            'pos.staff.attendance.subtitle',
            'Track your work hours and attendance'
          )}
        </p>
      </div>

      {/* Current Time */}
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-primary mb-2">
              {formatTime(currentTime)}
            </div>
            <div className="text-lg text-muted-foreground">
              {currentTime.toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5" />
              {translate(
                'pos.staff.attendance.current_status',
                'Current Status'
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshStatus}
              disabled={loading}
              className="flex items-center gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
              />
              {translate('pos.staff.attendance.refresh_status', 'Refresh')}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="flex items-center justify-between"
            data-testid="current-status"
          >
            <div className="flex items-center gap-3">
              {status.isCheckedIn ? (
                <>
                  {status.isOnBreak ? (
                    <>
                      <Timer className="h-8 w-8 text-orange-500" />
                      <div>
                        <div className="font-semibold text-orange-700">
                          {translate(
                            'pos.staff.attendance.status_on_break',
                            'On Break'
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {translate(
                            'pos.staff.attendance.status_since',
                            'Since'
                          )}{' '}
                          {status.breakStartTime
                            ? formatTime(new Date(status.breakStartTime))
                            : translate(
                                'pos.staff.attendance.unknown',
                                'Unknown'
                              )}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-8 w-8 text-green-500" />
                      <div>
                        <div className="font-semibold text-green-700">
                          {translate(
                            'pos.staff.attendance.checked_in',
                            'Checked In'
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {translate(
                            'pos.staff.attendance.status_since',
                            'Since'
                          )}{' '}
                          {status.lastCheckin
                            ? formatTime(status.lastCheckin)
                            : translate(
                                'pos.staff.attendance.unknown',
                                'Unknown'
                              )}
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <XCircle className="h-8 w-8 text-red-500" />
                  <div>
                    <div className="font-semibold text-red-700">
                      {translate(
                        'pos.staff.attendance.checked_out',
                        'Checked Out'
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {translate('pos.staff.attendance.status_since', 'Since')}{' '}
                      {status.lastCheckout
                        ? formatTime(status.lastCheckout)
                        : translate('pos.staff.attendance.unknown', 'Unknown')}
                    </div>
                  </div>
                </>
              )}
            </div>
            <Badge
              variant={
                status.isCheckedIn
                  ? status.isOnBreak
                    ? 'destructive'
                    : 'default'
                  : 'secondary'
              }
            >
              {status.isCheckedIn
                ? status.isOnBreak
                  ? translate(
                      'pos.staff.attendance.status_on_break',
                      'On Break'
                    )
                  : translate('pos.staff.attendance.status_working', 'Working')
                : translate('pos.staff.attendance.status_off_duty', 'Off Duty')}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Today's Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {translate('pos.staff.attendance.today_hours', "Today's Hours")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">
                {formatDuration(todayHours.totalHours)}
              </div>
              <div className="text-sm text-muted-foreground">
                {translate('pos.staff.attendance.total_hours', 'Total Hours')}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {formatDuration(todayHours.breakTime)}
              </div>
              <div className="text-sm text-muted-foreground">
                {translate('pos.staff.attendance.break_hours', 'Break Time')}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {formatDuration(todayHours.overtime)}
              </div>
              <div className="text-sm text-muted-foreground">
                {translate('pos.staff.attendance.overtime_hours', 'Overtime')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Check-in/Check-out Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {status.isCheckedIn
              ? translate('pos.staff.attendance.checkout', 'Check Out')
              : translate('pos.staff.attendance.checkin', 'Check In')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Location Status */}
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            <span className="text-sm">
              {location
                ? translate(
                    'pos.staff.attendance.location_detected',
                    'Location detected'
                  )
                : translate(
                    'pos.staff.attendance.location_not_available',
                    'Location not available'
                  )}
            </span>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">
              {translate(
                'pos.staff.attendance.notes_optional_label',
                'Notes (Optional)'
              )}
            </Label>
            <Textarea
              id="notes"
              placeholder={translate(
                'pos.staff.attendance.notes_placeholder',
                'Add any notes about your check-in/check-out...'
              )}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            <Button
              onClick={status.isCheckedIn ? handleCheckOut : handleCheckIn}
              disabled={loading}
              size="lg"
              className={`w-full max-w-xs ${
                status.isCheckedIn
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              } text-white`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {status.isCheckedIn
                    ? translate(
                        'pos.staff.attendance.checking_out',
                        'Checking Out...'
                      )
                    : translate(
                        'pos.staff.attendance.checking_in',
                        'Checking In...'
                      )}
                </>
              ) : (
                <>
                  {status.isCheckedIn ? (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      {translate('pos.staff.attendance.checkout', 'Check Out')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      {translate('pos.staff.attendance.checkin', 'Check In')}
                    </>
                  )}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coffee className="h-5 w-5" />
            {translate('pos.staff.dashboard.quick_actions', 'Quick Actions')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant={status.isOnBreak ? 'destructive' : 'outline'}
              className={`h-20 flex flex-col gap-2 ${
                status.isOnBreak
                  ? 'bg-orange-50 border-orange-200 hover:bg-orange-100'
                  : ''
              }`}
              onClick={async () => {
                console.log('🔍 Break Time button clicked!');
                if (!status.isCheckedIn) {
                  setBreakDialogOpen(true);
                } else if (status.isOnBreak) {
                  // End break - try API first, fallback to local state
                  try {
                    await attendanceService.endBreak(String(user?.id));
                    setStatus((prev) => ({
                      ...prev,
                      isOnBreak: false,
                      breakStartTime: undefined,
                    }));
                    console.log(
                      'Break ended via API at:',
                      new Date().toISOString()
                    );
                    notify('Break ended', { type: 'success' });
                    // Reload status and hours to reflect the change
                    await loadStatus();
                    await loadTodayHours();
                  } catch (error) {
                    console.error(
                      'API break end failed, using local state:',
                      error
                    );
                    // Fallback to local state management
                    setStatus((prev) => ({
                      ...prev,
                      isOnBreak: false,
                      breakStartTime: undefined,
                    }));
                    console.log(
                      'Break ended locally at:',
                      new Date().toISOString()
                    );
                    notify('Break ended (local)', { type: 'success' });
                    // Reload status and hours to reflect the change
                    await loadStatus();
                    await loadTodayHours();
                  }
                } else {
                  // Start break - try API first, fallback to local state
                  try {
                    const result = await attendanceService.startBreak(
                      String(user?.id)
                    );
                    setStatus((prev) => ({
                      ...prev,
                      isOnBreak: true,
                      breakStartTime: result.breakStartTime,
                    }));
                    console.log(
                      'Break started via API at:',
                      result.breakStartTime
                    );
                    notify('Break started', { type: 'success' });
                    // Reload hours to reflect the change
                    await loadTodayHours();
                  } catch (error) {
                    console.error(
                      'API break start failed, using local state:',
                      error
                    );
                    // Fallback to local state management
                    setStatus((prev) => ({
                      ...prev,
                      isOnBreak: true,
                      breakStartTime: new Date().toISOString(),
                    }));
                    console.log(
                      'Break started locally at:',
                      new Date().toISOString()
                    );
                    notify('Break started (local)', { type: 'success' });
                    // Reload hours to reflect the change
                    await loadTodayHours();
                  }
                }
              }}
            >
              <Timer
                className={`h-6 w-6 ${
                  status.isOnBreak ? 'text-orange-600' : ''
                }`}
              />
              <span
                className={
                  status.isOnBreak ? 'text-orange-700 font-semibold' : ''
                }
              >
                {!status.isCheckedIn
                  ? translate(
                      'pos.staff.attendance.break_quick_action_label',
                      'Break Time'
                    )
                  : status.isOnBreak
                  ? translate(
                      'pos.staff.attendance.break_quick_action_end',
                      'End Break'
                    )
                  : translate(
                      'pos.staff.attendance.break_quick_action_start',
                      'Start Break'
                    )}
              </span>
              {status.isOnBreak && status.breakStartTime && (
                <span className="text-xs text-orange-600">
                  {formatTime(new Date(status.breakStartTime))}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col gap-2"
              onClick={() => {
                console.log('🔍 View Timesheet button clicked!');

                // Try to find and click the attendance tab
                const attendanceTab = document.querySelector(
                  '[data-testid="attendance-tab"]'
                );

                if (attendanceTab) {
                  console.log('Found attendance tab, attempting to click...');

                  // Try multiple click methods
                  try {
                    // Method 1: Direct click
                    (attendanceTab as HTMLElement).click();
                    console.log('Clicked attendance tab directly');

                    // Method 2: Dispatch click event
                    const clickEvent = new MouseEvent('click', {
                      bubbles: true,
                      cancelable: true,
                      view: window,
                    });
                    attendanceTab.dispatchEvent(clickEvent);
                    console.log('Dispatched click event to attendance tab');

                    // Method 3: Focus and click
                    (attendanceTab as HTMLElement).focus();
                    (attendanceTab as HTMLElement).click();
                    console.log('Focused and clicked attendance tab');
                  } catch (error) {
                    console.log('Error clicking attendance tab:', error);
                  }

                  console.log('Navigating to timesheet...');
                } else {
                  console.log('Attendance tab not found');

                  // Fallback: Try to find any tab with "Attendance" text
                  const allTabs = document.querySelectorAll('[role="tab"]');
                  const attendanceTab = Array.from(allTabs).find((tab) => {
                    const tabText = tab.textContent?.toLowerCase();
                    return tabText?.includes('attendance');
                  });
                  if (attendanceTab) {
                    console.log('Found attendance tab by text');
                    (attendanceTab as HTMLElement).click();
                    console.log('Navigating to timesheet via text search...');
                  }
                }
              }}
            >
              <Calendar className="h-6 w-6" />
              <span>
                {translate(
                  'pos.staff.attendance.view_timesheet',
                  'View Timesheet'
                )}
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Break Time Dialog */}
      <Dialog open={breakDialogOpen} onOpenChange={setBreakDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate(
                'pos.staff.attendance.break_dialog_title',
                'Break Time'
              )}
            </DialogTitle>
            <DialogDescription>
              {!status.isCheckedIn
                ? translate(
                    'pos.staff.attendance.break_dialog_need_checkin',
                    'You need to check in first before taking a break.'
                  )
                : status.isOnBreak
                ? translate(
                    'pos.staff.attendance.break_dialog_end_question',
                    'You are currently on break. Do you want to end your break?'
                  )
                : translate(
                    'pos.staff.attendance.break_dialog_start_question',
                    'Do you want to start your break?'
                  )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBreakDialogOpen(false)}>
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            {!status.isCheckedIn ? (
              <Button
                onClick={() => {
                  // TODO: Implement check-in functionality
                  console.log('User wants to check in first');
                  setBreakDialogOpen(false);
                }}
              >
                {translate(
                  'pos.staff.attendance.break_dialog_checkin_first',
                  'Check In First'
                )}
              </Button>
            ) : status.isOnBreak ? (
              <Button
                onClick={() => {
                  // End break
                  setStatus((prev) => ({
                    ...prev,
                    isOnBreak: false,
                    breakStartTime: undefined,
                  }));
                  console.log('Break ended at:', new Date().toISOString());
                  setBreakDialogOpen(false);
                }}
              >
                {translate('pos.staff.attendance.end_break', 'End Break')}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  // Start break
                  setStatus((prev) => ({
                    ...prev,
                    isOnBreak: true,
                    breakStartTime: new Date().toISOString(),
                  }));
                  console.log('Break started at:', new Date().toISOString());
                  setBreakDialogOpen(false);
                }}
              >
                {translate('pos.staff.attendance.start_break', 'Start Break')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModernCheckinCheckout;
